import {
	buildBarcosPath,
	comunidadForProvince,
	slugify,
	type BarcosRoute,
	type TipoSegment
} from '$lib/geo';
import { listingUsesIncludedCaptain } from '$lib/listing-display';
import {
	faqSchemaAnswerText,
	faqSchemaQuestionName,
	ZARPAR_ORIGIN,
	type FaqSchemaItem
} from '$lib/utils/marketing-seo';
import {
	buildListingDetailSeoTitle,
	buildSeoTitle,
	normalizeSEOTitleText,
	resolveListingSeoLocation,
	titleContains,
	truncateSEOTitleAtWord
} from '$lib/utils/seo-title';
import { BoatsKindOptions, ListingsKindOptions } from '@/pocketbase/pocketbase-types';

export const LISTING_META_DESCRIPTION_MAX = 160;

const PROVINCE_ISO: Record<string, string> = {
	Pontevedra: 'PO',
	'A Coruña': 'C',
	Lugo: 'LU',
	Asturias: 'O',
	Cantabria: 'S',
	Guipúzcoa: 'SS',
	Vizcaya: 'BI',
	Barcelona: 'B',
	Gerona: 'GI',
	Tarragona: 'T',
	Valencia: 'V',
	Alicante: 'A',
	Castellón: 'CS',
	Murcia: 'MU',
	Almeria: 'AL',
	Granada: 'GR',
	Málaga: 'MA',
	Cádiz: 'CA',
	Sevilla: 'SE',
	Huelva: 'H',
	Mallorca: 'PM',
	Menorca: 'PM',
	Ibiza: 'PM',
	Formentera: 'PM',
	Cabrera: 'PM',
	Tenerife: 'TF',
	'La palma': 'TF',
	'El Hierro': 'TF',
	'La Gomera': 'TF',
	'Gran Canaria': 'GC',
	Lanzarote: 'GC',
	Fuerteventura: 'GC'
};

export interface ListingSeoPort {
	name?: string | null;
	city?: string | null;
	province?: string | null;
	country?: string | null;
}

export interface ListingSeoListing {
	id: string;
	title?: string | null;
	description?: string | null;
	kind: string;
	capitanMode?: string | null;
	spots?: number | null;
}

export interface ListingSeoBoat {
	kind?: (typeof BoatsKindOptions)[keyof typeof BoatsKindOptions] | null;
	builder?: string | null;
	model?: string | null;
	length?: number | null;
	horsepower?: number | null;
	constructionYear?: number | null;
	expand?: { port?: ListingSeoPort | null };
}

export interface ListingSeoReview {
	rating: number;
}

export interface ListingBreadcrumbItem {
	name: string;
	href?: string;
}

function boatTypeSeoLabel(
	boatKind?: (typeof BoatsKindOptions)[keyof typeof BoatsKindOptions] | null
): string {
	if (boatKind === BoatsKindOptions.sailboat) return 'velero';
	if (boatKind === BoatsKindOptions.motorboat) return 'lancha';
	return 'barco';
}

function boatTypeSchemaCategory(
	boatKind?: (typeof BoatsKindOptions)[keyof typeof BoatsKindOptions] | null
): string {
	if (boatKind === BoatsKindOptions.sailboat) return 'Sailboat';
	if (boatKind === BoatsKindOptions.motorboat) return 'Motorboat';
	return 'Boat';
}

function isExperienceListing(kind: string): boolean {
	return kind === ListingsKindOptions.experience;
}

function cityLabel(port?: ListingSeoPort | null): string {
	return normalizeSEOTitleText(port?.city ?? port?.name ?? port?.province ?? '');
}

function sanitizeSchemaDescription(text?: string | null, maxLen = 500): string {
	if (!text) return '';
	const plain = text.replace(/\s+/g, ' ').trim();
	return plain.length > maxLen ? truncateSEOTitleAtWord(plain, maxLen) : plain;
}

function truncateMetaDescription(text: string): string {
	const normalized = normalizeSEOTitleText(text);
	if (normalized.length <= LISTING_META_DESCRIPTION_MAX) return normalized;
	return truncateSEOTitleAtWord(normalized, LISTING_META_DESCRIPTION_MAX);
}

/** Title meta: armador title when it already matches intent; otherwise transactional template. */
export function buildListingMetaTitle(
	listing: ListingSeoListing,
	boat: ListingSeoBoat,
	isExperience = false
): string {
	const location = resolveListingSeoLocation(boat.expand?.port);
	const armadorTitle = normalizeSEOTitleText(listing.title ?? '');
	const city = cityLabel(boat.expand?.port);
	const tipo = boatTypeSeoLabel(boat.kind);
	const withCaptain = listingUsesIncludedCaptain({
		kind: listing.kind as never,
		capitanMode: listing.capitanMode as never
	});

	const hasLocationInTitle = !!city && titleContains(armadorTitle, city);
	const hasRentalIntent = /alquiler|salida|experiencia|charter|lancha|velero|barco/i.test(
		armadorTitle
	);

	if (armadorTitle && (hasLocationInTitle || hasRentalIntent)) {
		return buildListingDetailSeoTitle({
			listingTitle: armadorTitle,
			fallbackPrefix: isExperience ? 'Salida en barco' : 'Alquiler de',
			boatTypeLabel: isExperience
				? ''
				: tipo === 'lancha'
					? 'lancha'
					: tipo === 'velero'
						? 'velero'
						: 'barco',
			brandModel: [boat.builder, boat.model].filter(Boolean).join(' '),
			location
		});
	}

	let core: string;
	if (isExperience) {
		core = city ? `Salida en barco en ${city}` : 'Salida en barco';
	} else if (withCaptain) {
		core = city ? `Alquiler barco con patrón en ${city}` : 'Alquiler barco con patrón';
	} else if (boat.kind === BoatsKindOptions.sailboat) {
		core = city ? `Alquiler velero en ${city}` : 'Alquiler velero';
	} else if (boat.kind === BoatsKindOptions.motorboat) {
		core = city ? `Alquiler lancha en ${city} sin patrón` : 'Alquiler lancha sin patrón';
	} else {
		core = city ? `Alquiler barco en ${city}` : 'Alquiler barco';
	}

	return buildSeoTitle(core, [], { includeSuffix: true });
}

export function buildListingMetaDescription(input: {
	listing: ListingSeoListing;
	boat: ListingSeoBoat;
	minPrice?: number | null;
	listingTypeLabel?: string;
	isExperience?: boolean;
}): string {
	const { listing, boat, minPrice, listingTypeLabel, isExperience } = input;
	const tipo = boatTypeSeoLabel(boat.kind);
	const brandModel =
		boat.builder && boat.model
			? `${boat.builder} ${boat.model}`
			: boat.builder || boat.model || tipo;
	const port = boat.expand?.port;
	const location = port
		? `en ${port.name}${port.city ? `, ${port.city}` : port.province ? `, ${port.province}` : ''}`
		: '';
	const capacity = listing.spots ? `para ${listing.spots} personas` : '';
	const length = boat.length ? `de ${boat.length} m` : '';
	const pricePart = minPrice ? `desde ${minPrice}€/día. ` : '';
	const withCaptain = listingUsesIncludedCaptain({
		kind: listing.kind as never,
		capitanMode: listing.capitanMode as never
	});

	let description: string;

	if (isExperience) {
		description = `Salida en barco ${location} ${capacity}. ${listing.title || `Experiencia náutica en ${tipo}`}. ${pricePart}Reserva online en Zarpar.`;
	} else if (withCaptain) {
		description = `Alquiler de barco con patrón ${location}: ${brandModel} ${length} ${capacity}. ${pricePart}Reserva segura en Zarpar.`;
	} else {
		const typeLabel = listingTypeLabel?.toLowerCase() ?? 'alquiler';
		description = `Alquila ${brandModel} ${length} ${location} ${capacity}. ${listing.title || `Alquiler de ${tipo}`} disponible ${typeLabel}. ${pricePart}Reserva en Zarpar.`;
	}

	return truncateMetaDescription(description);
}

export function buildListingImageAlt(input: {
	listingTitle?: string | null;
	boatKind?: (typeof BoatsKindOptions)[keyof typeof BoatsKindOptions] | null;
	port?: ListingSeoPort | null;
	photoIndex: number;
	isExperience?: boolean;
}): string {
	const city = cityLabel(input.port);
	const tipo = boatTypeSeoLabel(input.boatKind);
	const title = normalizeSEOTitleText(input.listingTitle ?? '');
	const intent = input.isExperience ? 'Salida en barco' : `Alquiler ${tipo}`;
	const base = title ? `${intent} en ${city} — ${title}` : `${intent} en ${city}`;
	return `${base} (${input.photoIndex + 1})`;
}

export function buildListingOgImageAlt(
	listing: ListingSeoListing,
	boat: ListingSeoBoat,
	isExperience = false
): string {
	const parts = [boat.builder, boat.model].filter(Boolean).join(' ');
	const title = normalizeSEOTitleText(listing.title ?? 'Barco en alquiler');
	if (parts) return `${parts} — ${title}`;
	return title;
}

export function geoRegionCode(province?: string | null): string | null {
	if (!province) return null;
	const iso = PROVINCE_ISO[province];
	return iso ? `ES-${iso}` : null;
}

export function buildListingHubLinks(
	route: BarcosRoute | null,
	port?: ListingSeoPort | null
): { label: string; href: string }[] {
	if (!route?.comunidad) return [];

	const tipo: TipoSegment =
		route.tipo ?? (isExperienceListing(route.listingId ?? '') ? 'experiencia' : 'alquiler');
	const links: { label: string; href: string }[] = [];
	const city = port?.city?.trim();
	const portName = port?.name?.trim();

	if (city) {
		links.push({
			label: `Ver más barcos en ${city}`,
			href: buildBarcosPath({
				tipo: route.tipo ?? 'alquiler',
				comunidad: route.comunidad,
				provincia: route.provincia ?? port?.province ?? undefined,
				ciudad: city
			})
		});
	}

	if (portName && city) {
		links.push({
			label: `Barcos en ${portName}`,
			href: buildBarcosPath({
				tipo: route.tipo ?? 'alquiler',
				comunidad: route.comunidad,
				provincia: route.provincia ?? port?.province ?? undefined,
				ciudad: city,
				puerto: portName
			})
		});
	}

	if (route.provincia) {
		links.push({
			label: `Alquiler en ${route.provincia}`,
			href: buildBarcosPath({
				tipo: route.tipo ?? 'alquiler',
				comunidad: route.comunidad,
				provincia: route.provincia
			})
		});
	}

	links.push({
		label: route.tipo === 'experiencia' ? 'Experiencias náuticas' : 'Alquiler de barcos',
		href: buildBarcosPath({ tipo: route.tipo ?? 'alquiler', comunidad: route.comunidad })
	});

	return links;
}

export function buildListingBreadcrumbItems(input: {
	route: BarcosRoute | null;
	listingTitle?: string | null;
	port?: ListingSeoPort | null;
	listingKind?: string;
}): ListingBreadcrumbItem[] {
	const items: ListingBreadcrumbItem[] = [
		{ name: 'Zarpar', href: '/' },
		{ name: 'Barcos', href: '/barcos' }
	];

	const port = input.port;
	const comunidad =
		input.route?.comunidad ?? (port?.province ? comunidadForProvince(port.province) : undefined);
	const provincia = input.route?.provincia ?? port?.province ?? null;
	const city = port?.city ?? null;
	const portName = port?.name ?? null;
	const isExperience = isExperienceListing(input.listingKind ?? '');
	const tipo: TipoSegment = input.route?.tipo ?? (isExperience ? 'experiencia' : 'alquiler');

	const tipoLabel = tipo === 'experiencia' ? 'Experiencias' : 'Alquiler';
	items.push({
		name: tipoLabel,
		href: buildBarcosPath({ tipo })
	});

	if (comunidad) {
		items.push({
			name: comunidad.name,
			href: buildBarcosPath({ tipo, comunidad })
		});
	}

	if (comunidad && !comunidad.uniprovincial && provincia) {
		items.push({
			name: provincia,
			href: buildBarcosPath({ tipo, comunidad, provincia })
		});
	}

	if (city) {
		items.push({
			name: city,
			href: buildBarcosPath({
				tipo,
				comunidad: comunidad ?? undefined,
				provincia: provincia ?? undefined,
				ciudad: city
			})
		});
	}

	if (portName && city) {
		items.push({
			name: portName,
			href: buildBarcosPath({
				tipo,
				comunidad: comunidad ?? undefined,
				provincia: provincia ?? undefined,
				ciudad: city,
				puerto: portName
			})
		});
	}

	const title = normalizeSEOTitleText(input.listingTitle ?? 'Anuncio');
	items.push({
		name: title.length > 48 ? truncateSEOTitleAtWord(title, 48) : title
	});

	return items;
}

export function buildListingBreadcrumbSchema(
	items: ListingBreadcrumbItem[],
	pageUrl: string
): Record<string, unknown> {
	return {
		'@type': 'BreadcrumbList',
		'@id': `${pageUrl}#breadcrumb`,
		itemListElement: items.map((item, index) => {
			const entry: Record<string, unknown> = {
				'@type': 'ListItem',
				position: index + 1,
				name: item.name
			};
			if (item.href) {
				entry.item = item.href.startsWith('http')
					? item.href
					: `${ZARPAR_ORIGIN}${item.href.startsWith('/') ? item.href : `/${item.href}`}`;
			}
			return entry;
		})
	};
}

export function buildListingProductSchema(input: {
	listing: ListingSeoListing;
	boat: ListingSeoBoat;
	pageUrl: string;
	minPrice?: number | null;
	imageUrls?: string[];
	sellerName?: string;
	inStock?: boolean;
	reviews?: ListingSeoReview[];
}): Record<string, unknown> {
	const { listing, boat, pageUrl, minPrice, imageUrls, sellerName, inStock, reviews } = input;
	const brandModel =
		boat.builder && boat.model
			? `${boat.builder} ${boat.model}`
			: boat.builder || boat.model || boatTypeSeoLabel(boat.kind);

	const data: Record<string, unknown> = {
		'@type': 'Product',
		'@id': `${pageUrl}#product`,
		url: pageUrl,
		name: listing.title || `Alquiler de ${brandModel}`,
		description: sanitizeSchemaDescription(listing.description),
		brand: {
			'@type': 'Brand',
			name: boat.builder || 'Zarpar'
		},
		model: boat.model || undefined,
		category: boatTypeSchemaCategory(boat.kind),
		offers: {
			'@type': 'Offer',
			price: minPrice ?? undefined,
			priceCurrency: 'EUR',
			url: pageUrl,
			availability: inStock ? 'https://schema.org/InStock' : 'https://schema.org/OutOfStock',
			seller: {
				'@type': 'Person',
				name: sellerName || 'Armador'
			}
		},
		additionalProperty: [
			listing.spots
				? { '@type': 'PropertyValue', name: 'Capacidad', value: `${listing.spots} personas` }
				: null,
			boat.length
				? { '@type': 'PropertyValue', name: 'Eslora', value: `${boat.length} metros` }
				: null,
			boat.horsepower
				? { '@type': 'PropertyValue', name: 'Potencia', value: `${boat.horsepower} CV` }
				: null,
			boat.constructionYear
				? { '@type': 'PropertyValue', name: 'Año', value: boat.constructionYear }
				: null
		].filter(Boolean)
	};

	const port = boat.expand?.port;
	if (port) {
		data.location = {
			'@type': 'Place',
			name: port.name,
			address: {
				'@type': 'PostalAddress',
				addressLocality: port.city || port.name,
				addressRegion: port.province,
				addressCountry: port.country || 'ES'
			}
		};
	}

	if (imageUrls?.length) {
		data.image = imageUrls.length === 1 ? imageUrls[0] : imageUrls;
	}

	if (reviews && reviews.length > 0) {
		const total = reviews.reduce((sum, r) => sum + r.rating, 0);
		data.aggregateRating = {
			'@type': 'AggregateRating',
			ratingValue: (total / reviews.length).toFixed(1),
			reviewCount: reviews.length,
			bestRating: 5,
			worstRating: 1
		};
	}

	return data;
}

export function buildListingTouristTripSchema(input: {
	listing: ListingSeoListing;
	boat: ListingSeoBoat;
	pageUrl: string;
	minPrice?: number | null;
	imageUrls?: string[];
	providerName?: string;
	inStock?: boolean;
	reviews?: ListingSeoReview[];
}): Record<string, unknown> {
	const { listing, boat, pageUrl, minPrice, imageUrls, providerName, inStock, reviews } = input;
	const brandModel =
		boat.builder && boat.model
			? `${boat.builder} ${boat.model}`
			: boat.builder || boat.model || 'embarcación';

	const data: Record<string, unknown> = {
		'@type': 'TouristTrip',
		'@id': `${pageUrl}#trip`,
		url: pageUrl,
		name: listing.title || `Salida en barco — ${brandModel}`,
		description: sanitizeSchemaDescription(listing.description),
		touristType: 'Navegantes y amantes del mar',
		provider: {
			'@type': 'Person',
			name: providerName || 'Armador'
		},
		offers: {
			'@type': 'Offer',
			price: minPrice ?? undefined,
			priceCurrency: 'EUR',
			url: pageUrl,
			availability: inStock ? 'https://schema.org/InStock' : 'https://schema.org/OutOfStock'
		}
	};

	const port = boat.expand?.port;
	if (port) {
		data.location = {
			'@type': 'Place',
			name: port.name,
			address: {
				'@type': 'PostalAddress',
				addressLocality: port.city || port.name,
				addressRegion: port.province,
				addressCountry: port.country || 'ES'
			}
		};
	}

	if (imageUrls?.length) {
		data.image = imageUrls.length === 1 ? imageUrls[0] : imageUrls;
	}

	if (reviews && reviews.length > 0) {
		const total = reviews.reduce((sum, r) => sum + r.rating, 0);
		data.aggregateRating = {
			'@type': 'AggregateRating',
			ratingValue: (total / reviews.length).toFixed(1),
			reviewCount: reviews.length,
			bestRating: 5,
			worstRating: 1
		};
	}

	return data;
}

export function buildListingFaqSchema(
	faqs: FaqSchemaItem[],
	pageUrl: string,
	pageName: string
): Record<string, unknown> | null {
	if (faqs.length === 0) return null;

	return {
		'@type': 'FAQPage',
		'@id': `${pageUrl}#faq`,
		url: pageUrl,
		name: pageName,
		mainEntity: faqs.map((item) => ({
			'@type': 'Question',
			name: faqSchemaQuestionName(item.question),
			acceptedAnswer: {
				'@type': 'Answer',
				text: faqSchemaAnswerText(item.answer)
			}
		}))
	};
}

export function buildListingStructuredDataGraph(input: {
	breadcrumbItems: ListingBreadcrumbItem[];
	pageUrl: string;
	listing: ListingSeoListing;
	boat: ListingSeoBoat;
	minPrice?: number | null;
	imageUrls?: string[];
	sellerName?: string;
	inStock?: boolean;
	reviews?: ListingSeoReview[];
	faqs?: FaqSchemaItem[];
	isExperience?: boolean;
}): Record<string, unknown> | null {
	const graph: Record<string, unknown>[] = [
		buildListingBreadcrumbSchema(input.breadcrumbItems, input.pageUrl)
	];

	if (input.isExperience) {
		graph.push(
			buildListingTouristTripSchema({
				listing: input.listing,
				boat: input.boat,
				pageUrl: input.pageUrl,
				minPrice: input.minPrice,
				imageUrls: input.imageUrls,
				providerName: input.sellerName,
				inStock: input.inStock,
				reviews: input.reviews
			})
		);
	} else {
		graph.push(
			buildListingProductSchema({
				listing: input.listing,
				boat: input.boat,
				pageUrl: input.pageUrl,
				minPrice: input.minPrice,
				imageUrls: input.imageUrls,
				sellerName: input.sellerName,
				inStock: input.inStock,
				reviews: input.reviews
			})
		);
	}

	const pageName = input.listing.title || 'Anuncio náutico';
	const faqSchema = buildListingFaqSchema(input.faqs ?? [], input.pageUrl, pageName);
	if (faqSchema) graph.push(faqSchema);

	return {
		'@context': 'https://schema.org',
		'@graph': graph
	};
}

/** Build route segments from port + listing when URL route is available. */
export function listingRouteFromPort(
	listing: { id: string; kind: string },
	port?: ListingSeoPort | null
): BarcosRoute {
	const comunidad = port?.province ? comunidadForProvince(port.province) : null;
	const tipo: TipoSegment =
		listing.kind === ListingsKindOptions.experience ? 'experiencia' : 'alquiler';

	return {
		tipo,
		comunidad: comunidad ?? null,
		provincia: (port?.province as BarcosRoute['provincia']) ?? null,
		ciudadSlug: port?.city ? slugify(port.city) : null,
		puertoSlug: port?.name ? slugify(port.name) : null,
		listingId: listing.id
	};
}
